import React from 'react'
import { MenuItem } from '@common/material'
import {
  Folder as FolderIcon,
  ChevronRight,
  ExpandMore,
} from '@mui/icons-material'
import { useFileSystem } from '@core/react-query/features/folders/hooks'
import { Folder } from '@core/entities/folders/Folder'
import { Box, Flex, Text } from '@fundamentals'
import { CircularProgress } from '@mui/material'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import { FileTreeItem } from './FileTreeItem'
import { Palette } from '@core/theme/color-palette-siteassist'

interface FolderTreeNodeProps {
  folder: Folder
  organisationId: number
  projectId: number
  pl?: number
  isRoot?: boolean
  flatMode?: boolean
  multiSelect: boolean
  selectedSiteBoxDocuments: Array<SiteBoxDocument>
  expandedFolders?: Set<number>
  onToggleFolder?: (folderId: number) => void
  onSelectFile: (item: SiteBoxDocument) => void
  matchesFilter?: (file: SiteBoxDocument) => boolean
}

export const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
  folder: parentFolder,
  organisationId,
  projectId,
  pl = 1,
  isRoot = false,
  flatMode = false,
  multiSelect,
  selectedSiteBoxDocuments,
  expandedFolders,
  onToggleFolder,
  onSelectFile,
  matchesFilter,
}) => {
  const showHeader = !isRoot && !flatMode
  const isExpanded =
    isRoot || flatMode || (expandedFolders?.has(parentFolder.id) ?? false)

  const { folder, isLoading } = useFileSystem({
    organisationId,
    projectId,
    type: 'SITE_BOX',
    folderId: parentFolder.id,
    enabled: isExpanded,
    status: ['LIVE'],
  })

  const files = ((folder?.files ?? []) as SiteBoxDocument[]).filter(
    (file) => !matchesFilter || matchesFilter(file),
  )

  const childPl = isRoot ? pl : pl + 3

  const subfolderItems = (folder?.folders ?? []).map((subfolder) => (
    <FolderTreeNode
      key={subfolder.id}
      folder={subfolder}
      organisationId={organisationId}
      projectId={projectId}
      pl={childPl}
      flatMode={flatMode}
      multiSelect={multiSelect}
      selectedSiteBoxDocuments={selectedSiteBoxDocuments}
      expandedFolders={expandedFolders}
      onToggleFolder={onToggleFolder}
      onSelectFile={onSelectFile}
      matchesFilter={matchesFilter}
    />
  ))

  const fileItems = files.map((file) => (
    <FileTreeItem
      key={file.id}
      file={file}
      pl={flatMode ? undefined : childPl}
      showPath={flatMode}
      multiSelect={multiSelect}
      selectedSiteBoxDocuments={selectedSiteBoxDocuments}
      onSelectFile={onSelectFile}
    />
  ))

  const body = flatMode ? (
    <>
      {fileItems}
      {subfolderItems}
    </>
  ) : (
    <>
      {subfolderItems}
      {fileItems}
    </>
  )

  if (isRoot) {
    if (isLoading || !folder) return null
    return body
  }

  return (
    <>
      {showHeader && (
        <MenuItem
          onClick={() => onToggleFolder?.(parentFolder.id)}
          value={parentFolder.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
            pl,
            pr: 1,
          }}
        >
          <Flex py={0.6} alignItems='center'>
            {isExpanded ? (
              <ExpandMore sx={{ mr: 0.5, color: Palette.Grey[500] }} />
            ) : (
              <ChevronRight sx={{ mr: 0.5, color: Palette.Grey[500] }} />
            )}
            <FolderIcon sx={{ color: Palette.Grey[500] }} />
            <Text ml={1}>{parentFolder.displayName}</Text>
          </Flex>
        </MenuItem>
      )}

      {isExpanded && isLoading && (
        <Box display='flex' justifyContent='center' py={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isExpanded && !isLoading && folder && body}
    </>
  )
}
